import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function StaffRotaManager() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState({
    staff_name: '',
    role: '',
    day_of_week: 1,
    start_time: '09:00',
    end_time: '17:00',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const { data: rotas = [], isLoading } = useQuery({
    queryKey: ['staff-rotas'],
    queryFn: () => base44.entities.StaffRota.list('day_of_week'),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async () => {
    if (!form.staff_name) {
      toast({ title: 'Enter a staff name', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await base44.entities.StaffRota.create({ ...form, day_of_week: Number(form.day_of_week) });
      queryClient.invalidateQueries({ queryKey: ['staff-rotas'] });
      setForm({ staff_name: '', role: '', day_of_week: 1, start_time: '09:00', end_time: '17:00', notes: '' });
      toast({ title: 'Rota entry added' });
    } catch (e) {
      toast({ title: 'Failed to add', variant: 'destructive' });
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await base44.entities.StaffRota.delete(id);
      queryClient.invalidateQueries({ queryKey: ['staff-rotas'] });
    } catch (e) {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  const field = "bg-[#111115] border-white/10 text-white placeholder:text-white/20 focus:border-primary/60";

  const grouped = DAYS.map((d, i) => ({
    day: d,
    items: rotas.filter((r) => r.day_of_week === i),
  }));

  return (
    <div className="space-y-6">
      {/* Add form */}
      <div className="bg-[#111115] border border-white/5 p-6">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-4">ADD ROTA ENTRY</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label>Staff Name</Label>
            <Input value={form.staff_name} onChange={(e) => set('staff_name', e.target.value)} className={field} placeholder="e.g. Jamie" />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Input value={form.role} onChange={(e) => set('role', e.target.value)} className={field} placeholder="e.g. Instructor" />
          </div>
          <div className="space-y-1.5">
            <Label>Day</Label>
            <select
              value={form.day_of_week}
              onChange={(e) => set('day_of_week', Number(e.target.value))}
              className="w-full h-9 bg-[#111115] border border-white/10 text-white text-sm px-3 focus:border-primary/60 outline-none"
            >
              {DAYS.map((d, i) => (
                <option key={i} value={i}>{d}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Start Time</Label>
            <Input type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} className={field} />
          </div>
          <div className="space-y-1.5">
            <Label>End Time</Label>
            <Input type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} className={field} />
          </div>
        </div>
        <button
          onClick={add}
          disabled={saving}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-wider px-5 py-2.5 mt-4 transition-all disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} ADD TO ROTA
        </button>
      </div>

      {/* List grouped by day */}
      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <div key={g.day} className="bg-[#111115] border border-white/5">
              <div className="px-5 py-3 border-b border-white/5">
                <p className="text-white/40 text-[10px] font-bold tracking-widest">{g.day.toUpperCase()} · {g.items.length} STAFF</p>
              </div>
              {g.items.length === 0 ? (
                <p className="px-5 py-4 text-white/20 text-xs">No staff rostered.</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {g.items.map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-5 py-3">
                      <div>
                        <p className="text-white text-sm font-medium">{r.staff_name}{r.role ? <span className="text-white/30 text-xs"> · {r.role}</span> : null}</p>
                        <p className="text-white/30 text-xs flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" />{r.start_time} – {r.end_time}</p>
                        {r.notes && <p className="text-white/20 text-xs mt-1">{r.notes}</p>}
                      </div>
                      <button onClick={() => remove(r.id)} className="text-white/20 hover:text-red-400 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}